import { useSessionStorage } from '@/hooks/useSessionStorage'
import { InferFormValues, JsonFormPropertyV2, JsonFormSchemaV2 } from '../../component-library/FormComponent/types'
import { useGetPageFilters } from './useGetPageFilters'
import { useCallback, useMemo } from 'react'
import { format, parse, isValid } from 'date-fns'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'

export function useSubmitPageFilters() {
  const { pageFilterStorageKey: storageKey } = useAppContext()
  const {
    data: { type, data: schema },
  } = useGetPageFilters()
  const [filterSelectionsPersisted, setFiltersSelectionsPersisted, , isMounted] = useSessionStorage<
    Record<string, unknown>[]
  >(storageKey, [])
  const submitFilters = useCallback(
    (values: InferFormValues<JsonFormSchemaV2>) => {
      // Map property id -> format for normalization
      const properties: JsonFormPropertyV2[] = Array.isArray(schema?.properties) ? schema.properties : []
      const propertyFormatById: Record<string, string | undefined> = properties.reduce(
        (acc: Record<string, string | undefined>, prop: JsonFormPropertyV2) => {
          acc[prop.id] = prop.format
          return acc
        },
        {}
      )

      const normalizeDate = (val: unknown): unknown => {
        if (!val) return val
        if (val instanceof Date) return format(val, 'yyyy-MM-dd')
        if (typeof val === 'string') {
          const input = val.trim()
          // Try native parse for ISO-like strings
          const native = new Date(input)
          if (!Number.isNaN(native.getTime())) return format(native, 'yyyy-MM-dd')
          const patterns = ['yyyy-MM-dd', 'dd-MM-yyyy', 'yyyy/MM/dd', 'dd/MM/yyyy']
          for (const p of patterns) {
            const d = parse(input, p, new Date())
            if (isValid(d)) return format(d, 'yyyy-MM-dd')
          }
          const normalized = input.replace(/\//g, '-')
          for (const p of ['yyyy-MM-dd', 'dd-MM-yyyy']) {
            const d = parse(normalized, p, new Date())
            if (isValid(d)) return format(d, 'yyyy-MM-dd')
          }
        }
        return val
      }

      const propertyIds = new Set(Object.keys(propertyFormatById))
      const canonicalizeKey = (key: string): string => {
        if (propertyIds.has(key)) return key
        const withLeading = key.startsWith('/') ? key : `/${key}`
        if (propertyIds.has(withLeading)) return withLeading
        const withoutLeading = key.startsWith('/') ? key.slice(1) : key
        if (propertyIds.has(withoutLeading)) return withoutLeading
        return key
      }

      const changedEntries = Object.entries(values).map(([rawKey, value]) => {
        const key = canonicalizeKey(rawKey)
        const fmt = propertyFormatById[key]
        const normalizedValue = fmt === 'date' ? normalizeDate(value) : value
        return { key, value: normalizedValue }
      })

      const changedFilters = changedEntries.map(({ key, value }) => ({ [key]: value }))
      const unchangedFilters = filterSelectionsPersisted.filter(
        (item) => !new Set(changedEntries.map((e) => e.key)).has(Object.keys(item)[0])
      )
      setFiltersSelectionsPersisted([...changedFilters, ...unchangedFilters])
    },
    [filterSelectionsPersisted, setFiltersSelectionsPersisted, schema]
  )

  const defaultsFromPersisted: InferFormValues<JsonFormSchemaV2> | undefined = useMemo(
    () =>
      filterSelectionsPersisted && type === 'filter' ? Object.assign({}, ...filterSelectionsPersisted) : undefined,
    [filterSelectionsPersisted, type]
  )

  return useMemo(
    () => ({ submitFilters, defaultsFromPersisted, isMounted }),
    [submitFilters, defaultsFromPersisted, isMounted]
  )
}
